import * as React from 'react';
import { useDispatch } from 'react-redux';
import { toggleStickyScrollBtn } from '../slices';

type TContainerRef = React.RefObject<HTMLElement | null>;

function checkAndDispatch(
  element: HTMLElement | null,
  dispatch: (action: ReturnType<typeof toggleStickyScrollBtn>) => void,
) {
  if (!element) {
    return;
  }
  const rect = element.getBoundingClientRect();
  const isAboveViewport = rect.bottom < 0;
  dispatch(toggleStickyScrollBtn(isAboveViewport));
}

/** Хук проверяет, находится ли элемент выше текущего вьюпорта, и переключает видимость кнопки.
 * IntersectionObserver + scroll/touchmove/resize для стабильной работы на десктопе и мобильных (Safari/iOS). */
export function useToggleStickyScrollBtn(ref: TContainerRef) {
  const dispatch = useDispatch();
  const rafIdRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    const scheduleCheck = () => {
      if (rafIdRef.current !== null) {
        return;
      }
      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;
        checkAndDispatch(ref.current, dispatch);
      });
    };

    const observer = new IntersectionObserver(
      entries => {
        const [entry] = entries;
        if (!entry) {
          return;
        }
        const isAboveViewport = entry.boundingClientRect.bottom < 0;
        dispatch(toggleStickyScrollBtn(isAboveViewport));
      },
      { root: null, rootMargin: '0px', threshold: 0 },
    );

    observer.observe(element);

    const handleScrollOrResize = () => scheduleCheck();

    // Подписка на scroll и на document — на мобильных скролл может идти по document
    window.addEventListener('scroll', handleScrollOrResize, { passive: true });
    window.addEventListener('resize', handleScrollOrResize);
    // iOS: scroll часто срабатывает только по окончании скролла; touchmove даёт обновления во время жеста
    document.addEventListener('touchmove', handleScrollOrResize, { passive: true });

    // Начальная проверка после монтирования (ref уже привязан)
    const initialRaf = requestAnimationFrame(() => scheduleCheck());

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScrollOrResize);
      window.removeEventListener('resize', handleScrollOrResize);
      document.removeEventListener('touchmove', handleScrollOrResize);
      cancelAnimationFrame(initialRaf);
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [ref, dispatch]);
}
